import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../common/BackButton';
import {
    Plus,
    Minus,
    Save,
    AlertCircle
} from 'lucide-react';
import { customersAPI, invoicesAPI, tiresAPI, batteriesAPI, wiperProductsAPI, locationsAPI, zonesAPI, inventoriesAPI } from '../../services/garageApi';
import { InvoiceStatus } from '../../constants/enums';
import { sanitizeText, isNumberInRange } from '../../utils/securityUtils';
import { useDebounce } from '../../utils/performanceUtils';
import RateLimitError from '../common/RateLimitError';

const CreateInvoice = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rateLimitError, setRateLimitError] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [products, setProducts] = useState([]);
    const [zones, setZones] = useState([]);
    const [searchTerm] = useState('');

    // Map of "type-productId" → inventory records (for location/zone filtering)
    const [productInventoryMap, setProductInventoryMap] = useState({});

    const [invoiceData, setInvoiceData] = useState({
        customer_id: '',
        location_id: '',
        zone_id: '',
        invoice_number: '',
        issued_date: new Date().toISOString().split('T')[0],
        due_date: '',
        status: InvoiceStatus.DRAFT,
        notes: '',
        items: [
            {
                product_type: '',
                product_id: '',
                product_name: '',
                quantity: 1,
                unit_price: 0,
                total: 0
            }
        ],
        subtotal: 0,
        tax_rate: 0,
        tax_amount: 0,
        discount_amount: 0,
        total: 0
    });

    // Debounce search to reduce unnecessary renders
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Calculate totals whenever items, tax_rate, or discount_amount changes
    const calculateTotals = useCallback(() => {
        const subtotal = invoiceData.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
        const taxAmount = (subtotal * parseFloat(invoiceData.tax_rate)) / 100;
        const total = subtotal + taxAmount - parseFloat(invoiceData.discount_amount);

        setInvoiceData(prev => ({
            ...prev,
            subtotal,
            tax_amount: taxAmount,
            total: Math.max(0, total)
        }));
    }, [invoiceData.items, invoiceData.tax_rate, invoiceData.discount_amount]);

    useEffect(() => {
        fetchCustomers();
        fetchLocations();
        fetchProducts();
        generateInvoiceNumber();
    }, []);

    useEffect(() => {
        calculateTotals();
    }, [calculateTotals]);

    const fetchCustomers = async () => {
        try {
            const response = await customersAPI.getAll();
            const customersData = Array.isArray(response.data) ? response.data : [];
            setCustomers(customersData);
        } catch (err) {
            console.error('Error fetching customers:', err);
            const errorMsg = sanitizeText(err.response?.data?.message || 'Failed to load customers');
            setError(errorMsg);
            setCustomers([]);
        }
    };

    const fetchLocations = async () => {
        try {
            const response = await locationsAPI.getAll();
            const locationsData = Array.isArray(response.data) ? response.data : [];
            setLocations(locationsData);
        } catch (err) {
            console.error('Error fetching locations:', err);
            setLocations([]);
        }
    };

    const fetchProducts = async () => {
        try {
            const [tiresRes, batteriesRes, wipersRes] = await Promise.allSettled([
                tiresAPI.getAll(),
                batteriesAPI.getAll(),
                wiperProductsAPI.getAll()
            ]);

            let allProducts = [];

            // Process tires
            if (tiresRes.status === 'fulfilled' && tiresRes.value?.data) {
                const tiresArray = Array.isArray(tiresRes.value.data) ? tiresRes.value.data : [];
                const tires = tiresArray.map(tire => ({
                    ...tire,
                    product_type: 'tire',
                    name: sanitizeText(`${tire.brand?.name || 'Unknown'} ${tire.size || ''} ${tire.type || ''}`.trim()),
                    price: parseFloat(tire.selling_price || tire.price || 0)
                }));
                allProducts = [...allProducts, ...tires];
            }

            // Process batteries
            if (batteriesRes.status === 'fulfilled' && batteriesRes.value?.data) {
                const batteriesArray = Array.isArray(batteriesRes.value.data) ? batteriesRes.value.data : [];
                const batteries = batteriesArray.map(battery => ({
                    ...battery,
                    product_type: 'battery',
                    name: sanitizeText(`${battery.brand?.name || 'Unknown'} ${battery.type || ''} ${battery.voltage || ''}V ${battery.amperage || ''}Ah`.trim()),
                    price: parseFloat(battery.selling_price || battery.price || 0)
                }));
                allProducts = [...allProducts, ...batteries];
            }

            // Process wipers
            if (wipersRes.status === 'fulfilled' && wipersRes.value?.data) {
                const wipersArray = Array.isArray(wipersRes.value.data) ? wipersRes.value.data : [];
                const wipers = wipersArray.map(wiper => ({
                    ...wiper,
                    product_type: 'wiper',
                    name: sanitizeText(`${wiper.brand || 'Unknown'} ${wiper.size || ''} ${wiper.type || ''}`.trim()),
                    price: parseFloat(wiper.selling_price || wiper.price || 0)
                }));
                allProducts = [...allProducts, ...wipers];
            }

            setProducts(allProducts);
        } catch (err) {
            console.error('Error fetching products:', err);
            const errorMsg = sanitizeText(err.response?.data?.message || 'Failed to load products');
            setError(errorMsg);
            setProducts([]);
        }
    };

    // Fetch zones when location changes
    const fetchZones = useCallback(async (locationId) => {
        if (!locationId) {
            setZones([]);
            return;
        }
        try {
            const response = await zonesAPI.getAll({ location_id: locationId, per_page: 100 });
            const zonesData = Array.isArray(response.data) ? response.data : [];
            setZones(zonesData.filter(z => !z.is_system));
        } catch {
            setZones([]);
        }
    }, []);

    useEffect(() => {
        fetchZones(invoiceData.location_id);
        setInvoiceData(prev => ({ ...prev, zone_id: '' }));
    }, [invoiceData.location_id, fetchZones]);

    // Fetch inventory data for each selected product (to filter locations/zones)
    const selectedItems = invoiceData.items.filter(item => item.product_id && item.product_type);

    useEffect(() => {
        const fetchInventories = async () => {
            const newMap = { ...productInventoryMap };
            let changed = false;

            for (const item of selectedItems) {
                const key = `${item.product_type}-${item.product_id}`;
                if (newMap[key] === undefined) {
                    try {
                        const res = await inventoriesAPI.getByProduct(item.product_type, item.product_id);
                        newMap[key] = Array.isArray(res.data) ? res.data : [];
                    } catch {
                        newMap[key] = [];
                    }
                    changed = true;
                }
            }

            if (changed) setProductInventoryMap(newMap);
        };

        if (selectedItems.length > 0) {
            fetchInventories();
        }
    }, [selectedItems.map(i => `${i.product_type}-${i.product_id}`).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    // Compute available locations based on selected products' inventory
    const availableLocations = useMemo(() => {
        if (selectedItems.length === 0) return locations;

        const locationIds = new Set();

        for (const item of selectedItems) {
            const key = `${item.product_type}-${item.product_id}`;
            const inventories = productInventoryMap[key];
            if (!inventories || inventories.length === 0) {
                // Product has no inventory — show all locations
                return locations;
            }
            for (const inv of inventories) {
                if (inv.location_id) locationIds.add(String(inv.location_id));
            }
        }

        return locations.filter(loc => locationIds.has(String(loc.id)));
    }, [selectedItems, productInventoryMap, locations]);

    // Compute available zones for selected location based on products' inventory
    const availableZones = useMemo(() => {
        if (!invoiceData.location_id) return [];
        if (selectedItems.length === 0) return zones;

        const zoneIds = new Set();

        for (const item of selectedItems) {
            const key = `${item.product_type}-${item.product_id}`;
            const inventories = productInventoryMap[key];
            if (!inventories || inventories.length === 0) {
                return zones;
            }
            for (const inv of inventories) {
                if (String(inv.location_id) === String(invoiceData.location_id) && inv.zone_id) {
                    zoneIds.add(String(inv.zone_id));
                }
            }
        }

        return zones.filter(z => zoneIds.has(String(z.id)));
    }, [invoiceData.location_id, selectedItems, productInventoryMap, zones]);

    // Reset location if it's no longer available after product selection changes
    useEffect(() => {
        if (invoiceData.location_id && selectedItems.length > 0) {
            const isAvailable = availableLocations.some(loc => String(loc.id) === String(invoiceData.location_id));
            if (!isAvailable) {
                setInvoiceData(prev => ({ ...prev, location_id: '', zone_id: '' }));
            }
        }
    }, [availableLocations]); // eslint-disable-line react-hooks/exhaustive-deps

    const generateInvoiceNumber = () => {
        const timestamp = Date.now().toString().slice(-6);
        const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
        const invoiceNumber = `INV-${timestamp}-${random}`;
        setInvoiceData(prev => ({ ...prev, invoice_number: invoiceNumber }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Validate numeric inputs
        if (name === 'tax_rate' && !isNumberInRange(value, 0, 100)) {
            return;
        }
        if (name === 'discount_amount' && !isNumberInRange(value, 0, Infinity)) {
            return;
        }

        setInvoiceData(prev => ({
            ...prev,
            [name]: name === 'notes' ? sanitizeText(value) : value
        }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...invoiceData.items];
        newItems[index] = { ...newItems[index], [field]: value };

        // Auto-calculate item total
        if (field === 'quantity' || field === 'unit_price') {
            const quantity = parseFloat(newItems[index].quantity) || 0;
            const unitPrice = parseFloat(newItems[index].unit_price) || 0;
            newItems[index].total = quantity * unitPrice;
        }

        // If product is selected, auto-fill details
        if (field === 'product_id' && value) {
            const selectedProduct = products.find(p => p.id === value);
            if (selectedProduct) {
                newItems[index] = {
                    ...newItems[index],
                    product_name: selectedProduct.name,
                    product_type: selectedProduct.product_type,
                    unit_price: selectedProduct.price,
                    total: (parseFloat(newItems[index].quantity) || 1) * selectedProduct.price
                };
            }
        }

        setInvoiceData(prev => ({ ...prev, items: newItems }));
    };

    const addItem = () => {
        setInvoiceData(prev => ({
            ...prev,
            items: [
                ...prev.items,
                {
                    product_type: '',
                    product_id: '',
                    product_name: '',
                    quantity: 1,
                    unit_price: 0,
                    total: 0
                }
            ]
        }));
    };

    const removeItem = (index) => {
        if (invoiceData.items.length > 1) {
            const newItems = invoiceData.items.filter((_, i) => i !== index);
            setInvoiceData(prev => ({ ...prev, items: newItems }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setRateLimitError(null);

        // Validation
        if (!invoiceData.customer_id) {
            setError('Please select a customer');
            return;
        }

        if (!invoiceData.location_id) {
            setError('Please select a location');
            return;
        }

        if (invoiceData.items.length === 0 || !invoiceData.items[0].product_id) {
            setError('Please add at least one item');
            return;
        }

        // Validate status
        if (!InvoiceStatus.isValid(invoiceData.status)) {
            setError('Invalid invoice status');
            return;
        }

        try {
            setLoading(true);

            // Map frontend fields to backend expected fields
            const payload = {
                customer_id: invoiceData.customer_id,
                location_id: invoiceData.location_id,
                issued_date: invoiceData.issued_date,
                due_date: invoiceData.due_date || null,
                status: invoiceData.status,
                discount_amount: parseFloat(invoiceData.discount_amount) || 0,
                tax_amount: parseFloat(invoiceData.tax_amount) || 0,
                notes: invoiceData.notes || null,
                items: invoiceData.items.map(item => ({
                    invoiceable_type: item.product_type,  // backend getModelClass() handles alias mapping
                    invoiceable_id: item.product_id,
                    quantity: parseInt(item.quantity) || 1,
                    unit_price: parseFloat(item.unit_price) || 0,
                })),
            };

            await invoicesAPI.create(payload);
            alert('Invoice created successfully!');
            navigate('/invoices');
        } catch (err) {
            console.error('Error creating invoice:', err);

            // Handle rate limiting
            if (err.response?.status === 429) {
                setRateLimitError(err);
                return;
            }

            const errorMsg = sanitizeText(err.response?.data?.message || err.message || 'Failed to create invoice');
            setError('Error creating invoice: ' + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Memoize filtered products to avoid unnecessary recomputation
    const filteredProducts = useMemo(() => {
        if (!Array.isArray(products)) return [];
        if (!debouncedSearchTerm) return products;

        return products.filter(product =>
            product.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        );
    }, [products, debouncedSearchTerm]);

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <BackButton to="/invoices" />
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Create Invoice</h1>
                    <p className="text-gray-600">Create a new invoice for a customer</p>
                </div>
            </div>

            {/* Rate Limit Error */}
            {rateLimitError && (
                <RateLimitError
                    error={rateLimitError}
                    retryAfter={rateLimitError.retryAfter}
                    onRetry={() => {
                        setRateLimitError(null);
                        handleSubmit(new Event('submit'));
                    }}
                />
            )}

            {/* Error Message */}
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">{error}</div>
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Invoice Header */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Customer *
                            </label>
                            <select
                                name="customer_id"
                                value={invoiceData.customer_id}
                                onChange={handleInputChange}
                                required
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                <option value="">Select Customer</option>
                                {Array.isArray(customers) && customers.length > 0 ? (
                                    customers.map(customer => (
                                        <option key={customer.id} value={customer.id}>
                                            {customer.name} - {customer.email}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No customers available</option>
                                )}
                            </select>
                            {!Array.isArray(customers) || customers.length === 0 ? (
                                <p className="mt-1 text-sm text-gray-500">
                                    No customers found. Please add customers first.
                                </p>
                            ) : null}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Invoice Number
                            </label>
                            <input
                                type="text"
                                name="invoice_number"
                                value={invoiceData.invoice_number}
                                onChange={handleInputChange}
                                readOnly
                                className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Location *
                            </label>
                            <select
                                name="location_id"
                                value={invoiceData.location_id}
                                onChange={handleInputChange}
                                required
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                <option value="">Select Location</option>
                                {availableLocations.map(location => (
                                    <option key={location.id} value={location.id}>
                                        {location.name}
                                    </option>
                                ))}
                            </select>
                            {selectedItems.length > 0 && availableLocations.length === 0 && (
                                <p className="mt-1 text-xs text-yellow-600">No locations have inventory for the selected items.</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Zone
                            </label>
                            <select
                                name="zone_id"
                                value={invoiceData.zone_id}
                                onChange={handleInputChange}
                                disabled={!invoiceData.location_id}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                <option value="">All Zones</option>
                                {availableZones.map(zone => (
                                    <option key={zone.id} value={zone.id}>
                                        {zone.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Invoice Date *
                            </label>
                            <input
                                type="date"
                                name="issued_date"
                                value={invoiceData.issued_date}
                                onChange={handleInputChange}
                                required
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Due Date
                            </label>
                            <input
                                type="date"
                                name="due_date"
                                value={invoiceData.due_date}
                                onChange={handleInputChange}
                                min={invoiceData.issued_date}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                name="status"
                                value={invoiceData.status}
                                onChange={handleInputChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                                {InvoiceStatus.values().map(status => (
                                    <option key={status} value={status}>
                                        {InvoiceStatus.labels()[status]}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Invoice Items */}
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Invoice Items</h3>
                        <button
                            type="button"
                            onClick={addItem}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Item
                        </button>
                    </div>

                    <div className="space-y-4">
                        {invoiceData.items.map((item, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Product *
                                        </label>
                                        <select
                                            value={item.product_id}
                                            onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                                            required
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        >
                                            <option value="">Select Product</option>
                                            {filteredProducts.map(product => (
                                                <option key={`${product.product_type}-${product.id}`} value={product.id}>
                                                    {product.name} - ${product.price.toFixed(2)}
                                                </option>
                                            ))}
                                        </select>
                                        {filteredProducts.length === 0 && (
                                            <p className="mt-1 text-sm text-gray-500">
                                                No products available. Please add products first.
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Quantity *
                                        </label>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                            min="1"
                                            max="10000"
                                            required
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Unit Price *
                                        </label>
                                        <input
                                            type="number"
                                            value={item.unit_price}
                                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                                            step="0.01"
                                            min="0"
                                            required
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Total
                                        </label>
                                        <input
                                            type="text"
                                            value={`$${(parseFloat(item.total) || 0).toFixed(2)}`}
                                            readOnly
                                            className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
                                        />
                                    </div>

                                    <div>
                                        <button
                                            type="button"
                                            onClick={() => removeItem(index)}
                                            disabled={invoiceData.items.length === 1}
                                            title={invoiceData.items.length === 1 ? "At least one item required" : "Remove item"}
                                            className="inline-flex items-center p-2 border border-transparent rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Minus className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Invoice Totals */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Totals</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tax Rate (%)
                            </label>
                            <input
                                type="number"
                                name="tax_rate"
                                value={invoiceData.tax_rate}
                                onChange={handleInputChange}
                                step="0.01"
                                min="0"
                                max="100"
                                placeholder="0.00"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Discount Amount ($)
                            </label>
                            <input
                                type="number"
                                name="discount_amount"
                                value={invoiceData.discount_amount}
                                onChange={handleInputChange}
                                step="0.01"
                                min="0"
                                max={invoiceData.subtotal}
                                placeholder="0.00"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <div className="mt-6 border-t border-gray-200 pt-4">
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Subtotal:</span>
                                <span className="text-sm font-medium">${invoiceData.subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Tax ({invoiceData.tax_rate}%):</span>
                                <span className="text-sm font-medium">${invoiceData.tax_amount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Discount:</span>
                                <span className="text-sm font-medium text-red-600">-${parseFloat(invoiceData.discount_amount).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between border-t border-gray-200 pt-2">
                                <span className="text-lg font-medium text-gray-900">Total:</span>
                                <span className="text-lg font-bold text-blue-600">${invoiceData.total.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Notes */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Notes</h3>
                    <textarea
                        name="notes"
                        value={invoiceData.notes}
                        onChange={handleInputChange}
                        rows={3}
                        maxLength={1000}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Add any additional notes or terms..."
                    />
                    <p className="mt-1 text-sm text-gray-500">
                        {invoiceData.notes.length}/1000 characters
                    </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={() => navigate('/invoices')}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || rateLimitError}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Creating...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                Create Invoice
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateInvoice;
